import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {DEFAULT_SETTINGS, NAV} from "../data/siteData";
import {getSettings} from "../services/CommonService";

const SiteContext = createContext(null);

export const WIDE_BREAKPOINT = 900;

const sectionHref = (id) => id === 'top' ? (window.location.pathname + window.location.search) : ('#' + id);

const hashSection = () => {
    const key = (window.location.hash || '').replace(/^#/, '');
    return NAV.some(n => n.key === key) ? key : null;
};

// Shared page state: header size, breakpoint, the section in view, the mobile menu and the
// "get the app" dialog, and the contact settings from the API. The section in view is mirrored to the URL hash.
export const SiteProvider = ({children}) => {
    const [headerH, setHeaderH] = useState(68);
    const [wide, setWide] = useState(() => window.innerWidth >= WIDE_BREAKPOINT);
    const [section, setSection] = useState('top');
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [appOpen, setAppOpen] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const headerRef = useRef(null);
    const headerHRef = useRef(headerH);
    const sectionRef = useRef(section);

    // Fields left empty in the admin keep their default.
    useEffect(() => {
        let active = true;
        getSettings()
            .then(res => {
                const row = res.data && res.data.data && res.data.data[0];
                if (!active || !row) return;
                setSettings(current => {
                    const next = {...current};
                    Object.keys(current).forEach(key => {
                        const value = typeof row[key] === 'string' ? row[key].trim() : '';
                        if (value) next[key] = value;
                    });
                    return next;
                });
            })
            .catch(() => {
            });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        headerHRef.current = headerH;
        document.documentElement.style.setProperty('--pb-header-h', headerH + 'px');
    }, [headerH]);

    const scrollToId = useCallback((id, behavior = 'auto') => {
        const el = id === 'top' ? null : document.getElementById(id);
        const y = el ? window.scrollY + el.getBoundingClientRect().top - (headerHRef.current + 10) : 0;
        window.scrollTo({top: Math.max(0, y), behavior});
    }, []);

    const go = useCallback((id) => {
        setMenuOpen(false);
        // pushState rather than location.hash, so the browser does not jump before the smooth scroll.
        window.history.pushState(null, '', sectionHref(id));
        scrollToId(id, 'smooth');
    }, [scrollToId]);

    useEffect(() => {
        const onResize = () => {
            const el = headerRef.current;
            if (el) setHeaderH(Math.round(el.getBoundingClientRect().height));
            const isWide = window.innerWidth >= WIDE_BREAKPOINT;
            setWide(isWide);
            if (isWide) setMenuOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setAppOpen(false);
                setMenuOpen(false);
            }
        };
        // Solid header once the page moves; the last section whose top passed under the header is active.
        const onScroll = () => {
            setScrolled(window.scrollY > 24);
            let current = 'top';
            for (const n of NAV) {
                if (n.key === 'top') continue;
                const el = document.getElementById(n.key);
                if (el && el.getBoundingClientRect().top <= headerHRef.current + 48) current = n.key;
            }
            if (current !== sectionRef.current) {
                sectionRef.current = current;
                setSection(current);
                window.history.replaceState(null, '', sectionHref(current));
            }
        };
        const onPop = () => {
            setMenuOpen(false);
            scrollToId(hashSection() || 'top', 'smooth');
        };

        onResize();
        onScroll();
        window.addEventListener('resize', onResize);
        window.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, {passive: true});
        window.addEventListener('popstate', onPop);

        // Restore the section in the URL; re-measure a few times while images and the hero settle.
        const target = hashSection();
        const timers = [];
        if (target) {
            window.history.scrollRestoration = 'manual';
            const restore = () => scrollToId(target, 'auto');
            restore();
            [60, 250, 700].forEach(delay => timers.push(setTimeout(restore, delay)));
        }

        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('popstate', onPop);
            timers.forEach(clearTimeout);
        };
    }, [scrollToId]);

    const value = {
        headerRef, headerH, wide, section, scrolled, go,
        menuOpen, setMenuOpen,
        appOpen, settings,
        openApp: () => {
            setAppOpen(true);
            setMenuOpen(false);
        },
        closeApp: () => setAppOpen(false),
    };

    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);
